import { cloneDeep, flatten } from 'lodash';
import { AwsConfig, AwsConnectorModel } from './aws-connector.model';
import { SharepointConfig, SharepointConnectorModel } from './sharepoint-connector.model';
import { AzureOpenAIConfig, AzureOpenAIConnectorModel } from './azure-openai-connector.model';
import { AzureFoundryConfig, AzureFoundryConnectorModel } from './azure-foundry-connector.model';
import { JsonObject, sortByLabel, UiOption } from '@/lib/helpers';
import { ConnectorModel } from './connector.model';
import { WebcrawlerConfig, WebcralwerConnectorModel } from './webcrawler-connector.model';
import { NetskopeConfig, NetskopeConnectorModel } from './netskope-connector.model';
import { ZscalerConfig, ZscalerConnectorModel } from './zscaler-connector.model';
import { GeminiConnectorModel } from './gemini-connector.model';
import { OpenAIConnectorModel } from './openai-connector.model';
import { BraveAPIConnectorModel } from './brave-api-connector.model';
import { PerplexityConnectorModel } from './perplexity-connector.model';
import { ProxyConfig, ProxyConnectorModel } from './proxy-connector.model';
import { DohConfig, DohConnectorModel } from './doh-connector.model';
import { CloudflareConfig, CloudflareConnectorModel } from './cloudflare-connector.model';
import { CloudflareAIConfig, CloudflareAIConnectorModel } from './cloudflare-ai-connector.model';
import { TailscaleConfig, TailscaleConnectorModel } from './tailscale-connector.model';
import { AppConfig, AppConnectorModel } from './app-connector.model';
import { NvidiaNimConnectorModel } from './nvidia-nim-connector.model';
import { VertexAIConfig, VertexAIConnectorModel } from './vertex-ai-connector.model';
import { AnthropicConnectorModel } from './anthropic-connector.model';

export type ConnectorStatus = 'inactive' | 'pending' | 'active' | 'failed';

export type ConnectorType =
  | 'aws'
  | 'sharepoint-online'
  | 'azure-openai'
  | 'azure-foundry'
  | 'webcrawler'
  | 'netskope'
  | 'zscaler'
  | 'gemini'
  | 'openai'
  | 'brave-search'
  | 'proxy'
  | 'doh'
  | 'cloudflare'
  | 'cloudflare-ai'
  | 'perplexity'
  | 'tailscale'
  | 'app'
  | 'nvidia-nim'
  | 'vertex-ai'
  | 'anthropic';

export type ConnectorApiKeyConfig = {
  apiKey: string;
  apiKeyLast4?: string;
};

export type ConnectorConfig =
  | ConnectorApiKeyConfig
  | AwsConfig
  | SharepointConfig
  | AzureOpenAIConfig
  | AzureFoundryConfig
  | WebcrawlerConfig
  | NetskopeConfig
  | ZscalerConfig
  | ProxyConfig
  | DohConfig
  | CloudflareConfig
  | CloudflareAIConfig
  | TailscaleConfig
  | AppConfig
  | VertexAIConfig;

export type ConnectorUser = {
  _id: string;
  name: string;
  email: string;
};

export const ProxyConnectorAuthTypeOptions: UiOption[] = [
  {
    label: 'User',
    value: 'user',
    description:
      'Users are required to authenticate within a web browser. User names and email addresses will be shown in User Activity. Native applications require a user to have previously authenticated within a web browser.',
  },
  {
    label: 'Connector',
    value: 'connector',
    description:
      'The connector identity is used to apply policy and users are not prompted to authenticate for allowed services. Fingerprint information, rather than full user information, will be shown in User Activity. Native applications are supported without web browser authentication.',
  },
  {
    label: 'User with Connector Fallback',
    value: 'user-connector-fallback',
    description:
      'Users are prompted to authenticate within a web browser. When that is not possible, the connector identity is used to apply policy. A combination of full user information and fingerprints will be shown in User Activity. Native applications are supported without web browser authentication.',
    meta: {
      recommended: true,
    },
  },
];

export const hasConnectorAuthentication = (authenticationType: string) => {
  return ['user-connector-fallback', 'connector'].includes(authenticationType);
};

export const ConnectorMap: Map<ConnectorType, ConnectorModel> = new Map();

ConnectorMap.set('aws', new AwsConnectorModel());
ConnectorMap.set('sharepoint-online', new SharepointConnectorModel());
ConnectorMap.set('azure-openai', new AzureOpenAIConnectorModel());
ConnectorMap.set('azure-foundry', new AzureFoundryConnectorModel());
ConnectorMap.set('webcrawler', new WebcralwerConnectorModel());
ConnectorMap.set('netskope', new NetskopeConnectorModel());
ConnectorMap.set('zscaler', new ZscalerConnectorModel());
ConnectorMap.set('gemini', new GeminiConnectorModel());
ConnectorMap.set('openai', new OpenAIConnectorModel());
ConnectorMap.set('brave-search', new BraveAPIConnectorModel());
ConnectorMap.set('proxy', new ProxyConnectorModel());
ConnectorMap.set('cloudflare', new CloudflareConnectorModel());
ConnectorMap.set('doh', new DohConnectorModel());
ConnectorMap.set('cloudflare-ai', new CloudflareAIConnectorModel());
ConnectorMap.set('perplexity', new PerplexityConnectorModel());
ConnectorMap.set('tailscale', new TailscaleConnectorModel());
ConnectorMap.set('app', new AppConnectorModel());
ConnectorMap.set('nvidia-nim', new NvidiaNimConnectorModel());
ConnectorMap.set('vertex-ai', new VertexAIConnectorModel());
ConnectorMap.set('anthropic', new AnthropicConnectorModel());

export type ConnectorFeature = 'siem' | 'privateModels' | 'contextData' | 'ingress';

export type ConnectorFeatureSet = {
  siem: boolean;
  privateModels: boolean;
  contextData: boolean;
  ingress: boolean;
};

export const ConnectorFeatureLabels: Record<ConnectorFeature, string> = {
  siem: 'SIEM',
  privateModels: 'Private Models',
  contextData: 'Context Data',
  ingress: 'Ingress',
};

/*
 * Given a "generic" connector model, return a typed connector model
 * that includes all the type-specific config properties, so that
 * validation and other type-specific logic can be applied.
 * e.g. ConnectorModel -> AwsConnectorModel
 */
export const getTypedConnector = (model: ConnectorModel) => {
  const typedModel = ConnectorMap.get(model.type);

  if (!typedModel) {
    return model;
  }

  const Constructor = typedModel.constructor as new (data?: JsonObject) => typeof typedModel;

  const config = {
    ...typedModel.config,
    ...model.config,
  };

  const data = cloneDeep({ ...model._props, config });

  return new Constructor(data)!;
};

export const getConnectorOptions = (): UiOption[] => {
  return Array.from(ConnectorMap.entries()).map(([type, model]) => {
    const { typeLabel, supportedFeatures, comingSoon, deprecated, demo } = model;

    return {
      label: typeLabel,
      value: type as string,
      disabled: comingSoon,
      meta: {
        demo,
        deprecated,
        ...supportedFeatures,
      },
    };
  });
};

export const getConnectorCategoryOptions = (): UiOption[] => {
  const connectorOptions: UiOption[] = [];

  const allConnectorOptions = getConnectorOptions().filter(({ meta }) => !meta?.deprecated);

  // Ingress
  connectorOptions.push(
    {
      label: 'Network Ingress',
      value: 'ingress',
      meta: { header: true },
    },
    ...sortByLabel(allConnectorOptions.filter(({ meta }) => meta?.ingress)).map((opt) => ({
      ...opt,
      key: `${opt.value}-ingress`,
    }))
  );

  // Private Models
  connectorOptions.push(
    {
      label: 'Private models',
      value: 'privateModels',
      meta: { header: true },
    },
    ...sortByLabel(allConnectorOptions.filter(({ meta }) => meta?.privateModels)).map((opt) => ({
      ...opt,
      key: `${opt.value}-privateModels`,
    }))
  );

  // Context providers
  connectorOptions.push(
    {
      label: 'Data Sources',
      value: 'contextData',
      meta: { header: true },
    },
    ...sortByLabel(allConnectorOptions.filter(({ meta }) => meta?.contextData)).map((opt) => ({
      ...opt,
      key: `${opt.value}-contextData`,
    }))
  );

  return flatten(connectorOptions);
};
